import { randomFill } from 'crypto';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    isUnauthorized: boolean;
    role: string | null;
    clientId: number | null;
    accessToken: string | null;
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
    startDiscordLogin: () => Promise<void>;
    doGuestLogin: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [clientId, setClientId] = useState<number | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    // On mount: capture session_token from URL or localStorage, then verify with /api/me using Bearer auth
    useEffect(() => {
        (async () => {
            try {
                // 1) Read token from URL (?session_token=...) if present
                const url = new URL(window.location.href);
                const tokenFromUrl = url.searchParams.get('session_token');
                let token: string | null = null;

                if (tokenFromUrl) {
                    token = tokenFromUrl;
                    // Persist and clean URL
                    localStorage.setItem('session_token', token);
                    setAccessToken(token);
                    url.searchParams.delete('session_token');
                    window.history.replaceState({}, '', url.toString());
                } else {
                    // 2) An editor host pins the guest account with ?guest_id=,
                    //    so the client lands on the one the shard was told to
                    //    treat as an admin. A session token left over from
                    //    another guest is still perfectly valid, so it would win
                    //    the fallback below and the viewport would run as an
                    //    account no shard has any reason to promote — which
                    //    looks exactly like the editor being refused. Dropping
                    //    it is what makes the pin authoritative.
                    const pinnedGuestId = url.searchParams.get('guest_id');
                    if (pinnedGuestId && localStorage.getItem('guest_id') !== pinnedGuestId) {
                        localStorage.setItem('guest_id', pinnedGuestId);
                        localStorage.removeItem('session_token');
                    }

                    // 3) Fallback to existing token in localStorage
                    token = localStorage.getItem('session_token');
                    if (token) setAccessToken(token);
                }

                // If no token available, we're unauthenticated
                if (!token) {
                    setIsAuthenticated(false);
                    setIsUnauthorized(false);
                    return;
                }

                // 3) Validate token with /api/me using Bearer auth
                const res = await fetch('/api/me', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (res.ok) {
                    const data = await res.json();
                    setClientId(data.client_id);
                    setRole(data.role);
                    setIsAuthenticated(true);
                    setIsUnauthorized(false);
                } else if (res.status === 401) {
                    setIsAuthenticated(false);
                    setAccessToken(null);
                    localStorage.removeItem('session_token');
                    setIsUnauthorized(true);
                } else {
                    console.error('Unexpected /api/me status', res.status);
                }
            } catch (e) {
                console.error('Error calling /api/me', e);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Helper function to make authenticated API calls
    const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
        const token = accessToken || localStorage.getItem('session_token');
        if (!token) {
            setIsUnauthorized(true);
            setIsAuthenticated(false);
            throw new Error('Missing session token');
        }

        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        if (response.status === 401) {
            setIsUnauthorized(true);
            setIsAuthenticated(false);
        }
        return response;
    };

    const logout = async () => {
        try {
            // Attempt server-side logout if supported (optional)
            const token = accessToken || localStorage.getItem('session_token');
            if (token) {
                await fetch('/api/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });
            }
        } catch (e) {
            console.error('logout error', e);
        }
        localStorage.removeItem('admin_active_view');
        localStorage.removeItem('session_token');
        setClientId(null);
        setRole(null);
        setIsUnauthorized(false);
        setIsAuthenticated(false);
        setAccessToken(null);
        window.location.reload();
    };

    const startDiscordLogin = async () => {
        try {
            const redirect = encodeURIComponent(window.location.href.split('?')[0]);
            const res = await fetch(`/api/login_discord?redirect=${redirect}`, { credentials: 'include' });
            if (res.ok) {
                const url = await res.text();
                if (url.startsWith('https://discord.com/')) {
                    window.location.href = url;
                } else {
                    console.error('Unexpected login_discord response', url);
                }
            } else {
                console.error('login_discord failed', res.status);
            }
        } catch (e) {
            console.error('Discord login initiation error', e);
        }
    };

    const doGuestLogin = async () => {
        try {
            // An editor host can pin the guest account, so the client logs in as
            // the one the shard was told to treat as admin. Persisted, so a
            // reload without the parameter still lands on the same account.
            const pinnedGuestId = new URLSearchParams(window.location.search).get('guest_id');
            if (pinnedGuestId) {
                localStorage.setItem("guest_id", pinnedGuestId);
            }

            var guestIdStr = localStorage.getItem("guest_id");
            if (guestIdStr === null) {
                // God save me I don't have internet to look up how to do proper random ints
                guestIdStr = String(Math.floor(Math.random() * 99999999999));
                localStorage.setItem("guest_id", guestIdStr);
            }
            let guestId = parseInt(guestIdStr);
            const res = await fetch('/api/login_guest', {
                method: "POST",
                body: JSON.stringify({ guest_id: String(guestId) })
            });
            let tokenResponse = JSON.parse(await res.text());

            console.log()
            localStorage.setItem('session_token', tokenResponse.session_token);
            setAccessToken(tokenResponse.session_token);
        } catch (e) {
            console.error('Guest login error', e);
        }
    };

    const value: AuthContextType = {
        isAuthenticated,
        isLoading,
        isUnauthorized,
        role,
        clientId,
        accessToken,
        authenticatedFetch,
        startDiscordLogin,
        doGuestLogin,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
